/**
 * A parish's groups (spec 2026-10-02 hierarki, 5.1): its seksi / komisi and kelompok
 * kategorial, each with its kind, its name and its place (the parish itself or one of its
 * stasi). The defaults every parish starts with (`KELOMPOK_BAWAAN`) have ids derived from the
 * parish (`idKelompokBawaan`), so every device that writes them writes the same rows; the
 * device stamps them with its zero clock (apps/web/src/db/queries/pelayan.ts), so the pre-fill
 * never outranks a change made since. Until a default is changed or an assignment names it, it
 * is a suggestion Pelayan & pengurus's Hanya yang kosong leaves out (spec 2026-10-03 per
 * paroki, 9). The row rule a group obeys wherever it is written
 * (`periksaKelompok`, the push rule of step 4a); whether its place is the parish or a stasi of
 * it needs the tree and is the push route's (apps/api/src/routes/sync/kelompok.ts). Pure.
 */
import { JENIS_KELOMPOK } from './schema/contract'
import ui from './strings/ui.id.json' with { type: 'json' }
import type { LocalWrite } from './sync/ops'

export type JenisKelompok = (typeof JENIS_KELOMPOK)[number]

export type MasalahKelompok = 'paroki_id' | 'jenis' | 'nama' | 'unit_id'

const isi = (v: unknown): boolean => typeof v === 'string' && v.trim() !== ''

/** What a group as it will be stored lacks: its parish, a known kind, a name, a place. */
export function periksaKelompok(row: Record<string, unknown>): MasalahKelompok[] {
  const out: MasalahKelompok[] = []
  if (!isi(row.paroki_id)) out.push('paroki_id')
  if (typeof row.jenis !== 'string' || !(JENIS_KELOMPOK as readonly string[]).includes(row.jenis)) {
    out.push('jenis')
  }
  if (!isi(row.nama)) out.push('nama')
  if (!isi(row.unit_id)) out.push('unit_id')
  return out
}

/** "Seksi / komisi", "Kelompok kategorial"; an unknown kind reads as itself. */
export function labelJenisKelompok(jenis: string): string {
  return (ui.pelayan.jenisKelompok as Record<string, string>)[jenis] ?? jenis
}

export interface KelompokBawaan {
  /** Stable key of the default, part of its id; never shown. */
  kunci: keyof typeof ui.pelayan.kelompokBawaan
  jenis: JenisKelompok
  nama: string
}

const bawaan = (kunci: KelompokBawaan['kunci'], jenis: JenisKelompok): KelompokBawaan => ({
  kunci,
  jenis,
  nama: ui.pelayan.kelompokBawaan[kunci],
})

/** Spec 2026-10-02 hierarki, 5.1, in its order: four seksi, then six kelompok kategorial. */
export const KELOMPOK_BAWAAN: readonly KelompokBawaan[] = [
  bawaan('liturgi', 'seksi'),
  bawaan('katekese', 'seksi'),
  bawaan('kepemudaan', 'seksi'),
  bawaan('sosial', 'seksi'),
  bawaan('omk', 'kategorial'),
  bawaan('wkri', 'kategorial'),
  bawaan('legio', 'kategorial'),
  bawaan('misdinar', 'kategorial'),
  bawaan('koor', 'kategorial'),
  bawaan('sekami', 'kategorial'),
]

/** cyrb128: four 32-bit words of a string; not cryptographic, only stable and well spread. */
function cyrb128(teks: string): [number, number, number, number] {
  let h1 = 1779033703
  let h2 = 3144134277
  let h3 = 1013904242
  let h4 = 2773480762
  for (let i = 0; i < teks.length; i += 1) {
    const k = teks.charCodeAt(i)
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067)
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233)
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213)
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179)
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067)
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233)
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213)
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179)
  h1 ^= h2 ^ h3 ^ h4
  h2 ^= h1
  h3 ^= h1
  h4 ^= h1
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0]
}

/**
 * A default group's id: a UUID v8 (RFC 9562, custom) from the parish and the default's key,
 * the same on every device, so two devices that pre-fill offline write the same ten rows.
 */
export function idKelompokBawaan(parokiId: string, kunci: string): string {
  const hex = cyrb128(`kelompok:${parokiId}:${kunci}`)
    .map((h) => h.toString(16).padStart(8, '0'))
    .join('')
  const varian = ((Number.parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16)
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `8${hex.slice(13, 16)}`,
    `${varian}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-')
}

/**
 * The writes of a parish's defaults the device does not hold yet (`ada`: the ids it holds,
 * deleted or not), at the parish itself, active, in the spec's order. Paroki baru writes all
 * ten with the new parish; the device's first use writes what is missing.
 */
export function tulisanKelompokBawaan(
  parokiId: string,
  ada: ReadonlySet<string> = new Set(),
): LocalWrite[] {
  const out: LocalWrite[] = []
  KELOMPOK_BAWAAN.forEach((k, i) => {
    const rowId = idKelompokBawaan(parokiId, k.kunci)
    if (ada.has(rowId)) return
    out.push({
      table: 'kelompok',
      row_id: rowId,
      fields: {
        paroki_id: parokiId,
        jenis: k.jenis,
        nama: k.nama,
        unit_id: parokiId,
        aktif: true,
        urutan: i + 1,
      },
    })
  })
  return out
}
